import React from "react";
import TableAdv from "../../Table/TableAdv/TableAdv";
import NoData from "../../Info/NoData";
import { config as baseConfig, tableConfig } from "../config";

const keyFromName = (name) =>
  String(name || "series")
    .toLowerCase()
    .replace(/[^a-z0-9_]+/gi, "_")
    .replace(/^_+|_+$/g, "");

const isISODateLike = (v) =>
  typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v.trim());

const sumSeries = (data, n) => {
  let sum = 0;
  let hasAny = false;
  for (let i = 0; i < n; i += 1) {
    const v = Array.isArray(data) && i < data.length ? data[i] : null;
    if (v == null) continue;
    const num = Number(v);
    if (!isFinite(num)) continue;
    sum += num;
    hasAny = true;
  }
  return hasAny ? sum : null;
};

const TableChartNew = ({
  series = [],
  categories = [],
  componentId,
  width = "100%",
  height = 360,
  tableConfig: tableConfigOverride,
  showTotalRow,
}) => {
  const safeOverride =
    tableConfigOverride && typeof tableConfigOverride === "object"
      ? tableConfigOverride
      : null;

  const resolvedTableConfig = {
    ...tableConfig,
    ...(safeOverride || {}),
    toolbarRight: {
      ...(tableConfig?.toolbarRight || {}),
      ...(safeOverride?.toolbarRight || {}),
    },
    toolbarLeft: {
      ...(tableConfig?.toolbarLeft || {}),
      ...(safeOverride?.toolbarLeft || {}),
    },
  };

  const showTotalRowResolved =
    typeof showTotalRow === "boolean"
      ? showTotalRow
      : (resolvedTableConfig?.showTotalRow ?? false);

  const baseColumns = Array.isArray(resolvedTableConfig.columns)
    ? resolvedTableConfig.columns
    : [];

  const isGroupedStacked = (arr) =>
    (Array.isArray(arr) ? arr : []).some((s) => typeof s?.__group === "number");

  const buildGroupedMeta = (arr) => {
    const groupsById = new Map();
    const innerNames = new Set();
    for (const s of Array.isArray(arr) ? arr : []) {
      if (typeof s?.__group === "number") {
        const id = Number(s.__group);
        if (!groupsById.has(id)) {
          groupsById.set(id, {
            id,
            name:
              typeof s.__groupName === "string" && s.__groupName.trim()
                ? s.__groupName
                : `Group ${id + 1}`,
            seriesId: s?.__groupSeriesId ?? null,
          });
        } else if (
          groupsById.get(id)?.seriesId == null &&
          s?.__groupSeriesId != null
        ) {
          groupsById.set(id, {
            ...groupsById.get(id),
            seriesId: s.__groupSeriesId,
          });
        }
      }
      if (typeof s?.name === "string" && s.name.trim()) innerNames.add(s.name);
    }
    const groups = Array.from(groupsById.values()).sort((a, b) => a.id - b.id);
    return { groups, innerNames: Array.from(innerNames) };
  };

  const makeInnerColumns = (names) => {
    const used = new Set();
    return (Array.isArray(names) ? names : []).map((name, idx) => {
      const base = keyFromName(name || `series_${idx + 1}`);
      let key = base;
      let n = 1;
      while (used.has(key)) {
        key = `${base}_${n++}`;
      }
      used.add(key);
      return {
        key,
        label: name || key,
        dataType: "number",
        total: showTotalRowResolved,
        render: (row) => row[key],
      };
    });
  };

  const groupedStacked = isGroupedStacked(series);
  const { groups, innerNames } = groupedStacked
    ? buildGroupedMeta(series)
    : {
        groups: [],
        innerNames: (Array.isArray(series) ? series : []).map(
          (s) => s?.name || "",
        ),
      };

  const innerColumns = groupedStacked
    ? makeInnerColumns(innerNames)
    : (() => {
        const used = new Set();
        return (Array.isArray(series) ? series : []).map((s, idx) => {
          const base = keyFromName(s?.key || s?.name || `series_${idx + 1}`);
          let key = base;
          let n = 1;
          while (used.has(key)) key = `${base}_${n++}`;
          used.add(key);
          return {
            key,
            label: s?.name || key,
            dataType: "number",
            total: showTotalRowResolved,
            render: (row) => row[key],
          };
        });
      })();

  const hasCategoryInBase = baseColumns.some((c) => c?.key === "category");
  const categoryColumnDef =
    resolvedTableConfig?.categoryColumn &&
    typeof resolvedTableConfig.categoryColumn === "object" &&
    resolvedTableConfig.categoryColumn
      ? resolvedTableConfig.categoryColumn
      : {
          key: "category",
          label: "Category",
          width: 260,
          dataType: "string",
        };
  const categoryColumn = hasCategoryInBase ? [] : [categoryColumnDef];

  const hasGroupInBase = baseColumns.some((c) => c?.key === "group");
  const groupColumnDef =
    resolvedTableConfig?.groupColumn &&
    typeof resolvedTableConfig.groupColumn === "object" &&
    resolvedTableConfig.groupColumn
      ? resolvedTableConfig.groupColumn
      : { key: "group", label: "Group", width: 180, dataType: "string" };
  const groupColumn = groupedStacked && !hasGroupInBase ? [groupColumnDef] : [];

  const columns = [
    ...baseColumns,
    ...categoryColumn,
    ...groupColumn,
    ...innerColumns,
  ];

  const mapTableRows = (arr, cats) => {
    const nCats = Array.isArray(cats) && cats.length > 0 ? cats.length : null;
    const seriesLen =
      Array.isArray(arr?.[0]?.data) && arr[0].data.length > 0
        ? arr[0].data.length
        : 0;
    const n = nCats != null ? nCats : seriesLen;
    if (!n || n <= 0) return [];
    const shouldAggregateRange =
      nCats != null &&
      nCats > 1 &&
      Array.isArray(cats) &&
      cats.length === n &&
      cats.every(isISODateLike);
    const rangeLabel =
      shouldAggregateRange && cats.length > 0
        ? `${cats[0]} to ${cats[cats.length - 1]}`
        : null;
    if (!groupedStacked) {
      if (shouldAggregateRange) {
        const row = { id: "range", category: rangeLabel };
        innerColumns.forEach((col, idx) => {
          const s = arr[idx];
          row[col.key] = sumSeries(s?.data, n);
        });
        return [row];
      }
      return Array.from({ length: n }, (_, i) => {
        const row = {
          id: i,
          category:
            nCats != null && Array.isArray(cats) ? cats[i] : String(i + 1),
        };
        innerColumns.forEach((col, idx) => {
          const s = arr[idx];
          const val =
            Array.isArray(s?.data) && i < s.data.length ? s.data[i] : null;
          row[col.key] = val;
        });
        return row;
      });
    }

    const byGroupAndInner = new Map();
    for (const s of Array.isArray(arr) ? arr : []) {
      if (typeof s?.__group !== "number") continue;
      const inner =
        typeof s?.name === "string" && s.name.trim() ? s.name : "Series";
      byGroupAndInner.set(`${Number(s.__group)}::${inner}`, s);
    }

    const rows = [];
    if (shouldAggregateRange) {
      for (const g of groups) {
        const row = {
          id: `range-${g.id}`,
          category: rangeLabel,
          group: g.name,
          seriesId: g.seriesId,
        };
        innerColumns.forEach((col) => {
          const innerName = col.label;
          const s = byGroupAndInner.get(`${g.id}::${innerName}`);
          row[col.key] = sumSeries(s?.data, n);
        });
        rows.push(row);
      }
      return rows;
    }
    for (let i = 0; i < n; i += 1) {
      const category =
        nCats != null && Array.isArray(cats) ? cats[i] : String(i + 1);
      for (const g of groups) {
        const row = {
          id: `${i}-${g.id}`,
          category,
          group: g.name,
          seriesId: g.seriesId,
        };
        innerColumns.forEach((col) => {
          const innerName = col.label;
          const s = byGroupAndInner.get(`${g.id}::${innerName}`);
          const val =
            Array.isArray(s?.data) && i < s.data.length ? s.data[i] : null;
          row[col.key] = val;
        });
        rows.push(row);
      }
    }
    return rows;
  };

  const rows = mapTableRows(series, categories);
  const isEmpty = innerColumns.length === 0 || rows.length === 0;
  const noDataCfg = baseConfig?.noDataConfig || {};
  const showNoData =
    typeof noDataCfg.show === "boolean" ? noDataCfg.show : true;

  return (
    <div
      style={{
        width: typeof width === "number" ? `${width}px` : width,
        height: typeof height === "number" ? `${height}px` : height,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {isEmpty ? (
        showNoData ? (
          <NoData
            title={noDataCfg.title}
            desc={noDataCfg.desc}
            color={noDataCfg.color}
            noImage={noDataCfg.noImage}
          />
        ) : (
          <div className="w-full h-full relative p-6">
            <div className="w-full h-full flex flex-col gap-3">
              <div className="w-full grid grid-cols-4 gap-3">
                {[0, 1, 2, 3].map((i) => (
                  <div key={`h-${i}`} className="h-6 bg-gray-200 rounded" />
                ))}
              </div>
              <div className="flex flex-col gap-2">
                {[0, 1, 2, 3, 4, 5].map((r) => (
                  <div key={`r-${r}`} className="w-full grid grid-cols-4 gap-3">
                    {[0, 1, 2, 3].map((c) => (
                      <div
                        key={`c-${r}-${c}`}
                        className="h-5 bg-gray-100 rounded"
                      />
                    ))}
                  </div>
                ))}
              </div>
            </div>
            <div className="absolute bottom-4 left-0 right-0 text-center text-sm text-gray-500">
              No data available
            </div>
          </div>
        )
      ) : (
        <TableAdv
          columns={columns}
          rows={rows}
          tableId={null}
          hideToolBar={resolvedTableConfig.hideToolBar}
          showPages={resolvedTableConfig.showPages}
          showSlNo={resolvedTableConfig.showSlNo}
          showCheckbox={resolvedTableConfig.showCheckbox}
          fixedCol={resolvedTableConfig.fixedCol}
          totalText={resolvedTableConfig.totalText}
          toolbarRight={resolvedTableConfig.toolbarRight}
          toolbarLeft={resolvedTableConfig.toolbarLeft}
        />
      )}
    </div>
  );
};

export default TableChartNew;
